import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { userPool } from './cognito';
import type { OAuthTokens } from './google';

/** Caller identity decoded from the ID token (mirrors the API's view). */
export interface Profile {
  readonly sub: string;
  readonly email: string;
  readonly name: string;
  readonly groups: readonly string[];
}

export const profileFromSession = (session: CognitoUserSession): Profile => {
  const p = session.getIdToken().payload;
  const groups = Array.isArray(p['cognito:groups']) ? (p['cognito:groups'] as string[]) : [];
  const email = typeof p.email === 'string' ? p.email : '';
  return {
    sub: typeof p.sub === 'string' ? p.sub : '',
    email,
    name: typeof p.name === 'string' && p.name ? p.name : email,
    groups,
  };
};

/**
 * Current cached session (refreshing via the refresh token if expired); null if signed out.
 *
 * This MUST always resolve — the app gates its first render on it (a 'loading' splash), so a
 * hung Cognito callback (corrupt/stale localStorage, a stalled refresh request) would otherwise
 * trap a signed-out visitor on the splash instead of redirecting to /login. We guard with a
 * timeout, a try/catch, and a single-resolve latch.
 */
export const getSession = (): Promise<CognitoUserSession | null> =>
  new Promise((resolve) => {
    let settled = false;
    const finish = (session: CognitoUserSession | null) => {
      if (settled) return;
      settled = true;
      resolve(session);
    };
    // Safety net: never let the app sit in 'loading' forever.
    const timer = setTimeout(() => finish(null), 6000);
    try {
      const user = userPool.getCurrentUser();
      if (!user) {
        clearTimeout(timer);
        return finish(null);
      }
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        clearTimeout(timer);
        finish(err ? null : session);
      });
    } catch {
      clearTimeout(timer);
      finish(null);
    }
  });

/** Valid ID token (refreshed if needed) for API calls. Throws if not authenticated. */
export const getIdToken = async (): Promise<string> => {
  const session = await getSession();
  if (!session) throw new Error('Not authenticated');
  return session.getIdToken().getJwtToken();
};

/**
 * Cache tokens obtained from the Google code exchange into the same localStorage store the
 * SDK uses, so getSession()/getIdToken()/refresh all work uniformly with the email/pw path.
 */
export const hydrateFromTokens = (tokens: OAuthTokens): Profile => {
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.id_token }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.access_token }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refresh_token }),
  });
  const username = String(
    session.getAccessToken().payload.username ?? session.getIdToken().payload.sub,
  );
  const user = new CognitoUser({ Username: username, Pool: userPool });
  user.setSignInUserSession(session); // persists to localStorage
  return profileFromSession(session);
};

export const signOutCurrent = (): void => {
  userPool.getCurrentUser()?.signOut();
};
